import React from 'react';
import { useIntl } from "react-intl";
import { clickFromKeyboard } from '@/util/keyboardUtil';
import { UTCToLocalString } from "@/util/dateUtil";
import { usePaginatedScenarios } from "@/query/scenario";
import { useModal, usePageParam, useReactGA, useRouteHandler } from "@/hook";
import Button from "@/component/Button";
import { SearchBar } from "@/component/input";
import AccountPopover from "@/component/AccountPopover";
import PaginationBar from "@/component/PaginationBar";
import AppMenu from '@/component/AppMenu';
import HighlightText from '@/component/HighlightText';
import { AddIcon } from "@/assets/svgs/icons";

import ScenarioCreateModal, { SCENARIO_CREATE_MODAL_ID } from './component/ScenarioCreateModal';


const SCENARIO_PAGE_SIZE = 15;

interface ScenarioListPageParam {
    page: number;
    size: number;
    keyword?: string;
}

const ScenarioListView: React.FC = () => {
    const intl = useIntl();
    const { moveToDashboard } = useRouteHandler();
    const modal = useModal();
    
    const { param, setParam } = usePageParam<ScenarioListPageParam>({
        page: 0,
        size: SCENARIO_PAGE_SIZE,
    });

    const { data: scenarioPage } = usePaginatedScenarios(
        param.page,
        param.size,
        param.keyword
    );

    useReactGA();

    return (<>
        <ScenarioCreateModal />

        <div className="size-full flex flex-col bg-black-100">
            <div className="h-[72px] px-3 py-[15px] flex justify-between bg-chat42">
                <div className="flex items-center gap-2 rounded-[6px]">
                    <AppMenu className="text-white hover:text-chat42 hover:bg-chat42-20" />
                    <div className="p-1 flex flex-col gap-0.5 cursor-default">
                        <div className="text-white text-[14px] font-[600] leading-none">
                            Chat42
                        </div>
                        <div className="text-white text-[17px] font-[700] leading-none">
                            Conversation Maker
                        </div>
                    </div>
                </div>
                <AccountPopover anchor="bottom end"
                                className="mt-2"
                />
            </div>
            <div className="w-full flex-1 p-5 flex min-[832px]:justify-center overflow-y-hidden overflow-x-auto">
                <div className="w-md-page h-full flex flex-col items-center">
                    <SearchBar placeholder={intl.formatMessage({ id: "i00019" })}
                               formAriaLabel={intl.formatMessage({ id: "b0014" })}
                               clearButtonAriaLabel={intl.formatMessage({ id: "b0042" })}
                               onSearch={keyword => setParam({ ...param, keyword })}
                    />
                    <div className="w-full mt-3 flex items-center justify-between">
                        <div className="ml-0.5">
                            {`${intl.formatMessage({ id: "b0014" })} (${scenarioPage?.total ?? 0})`}
                        </div>

                        <Button variant="text"
                                className="my-1"
                                onClick={() => modal.open(SCENARIO_CREATE_MODAL_ID)}
                        >
                            <AddIcon className="size-4"
                                     aria-hidden="true"
                            />
                            {intl.formatMessage({ id: "i00023" })}
                        </Button>
                    </div>

                    <div role="table"
                         aria-label={intl.formatMessage({ id: "b0014" })}
                         className="selection-table mt-1"
                    >
                        <div role="rowgroup"
                             className="selection-table-thead grid-table-scenario"
                        >
                            <div role="columnheader"
                                 className=" selection-table-th pl-[25px]"
                            >
                                {intl.formatMessage({ id: "b0050" })}
                            </div>
                            <div role="columnheader"
                                 className=" selection-table-th pl-[15px]"
                            >
                                {intl.formatMessage({ id: "b0051" })}
                            </div>
                            <div role="columnheader"
                                 className=" selection-table-th pl-[15px]"
                            >
                                {intl.formatMessage({ id: "i00027" })}
                            </div>
                            <div role="columnheader"
                                 className=" selection-table-th pl-[15px]"
                            >
                                {intl.formatMessage({ id: "i00028" })}
                            </div>
                        </div>
                        <div role="rowgroup"
                             className="selection-table-tbody"
                        >
                            {scenarioPage?.content.map(scenario => (
                                <div key={scenario.id}
                                     role="row"
                                     tabIndex={0}
                                     aria-label={scenario.name}
                                     className="selection-table-tr grid-table-scenario hover:bg-chat42-5 cursor-pointer"
                                     onClick={() => moveToDashboard(scenario.id)}
                                     onKeyDown={clickFromKeyboard}
                                >
                                    <div role="cell"
                                         className="selection-table-td pl-[25px]"
                                    >
                                        <HighlightText query={scenario.name}
                                                       keyword={param.keyword}
                                                       highlightClassName="text-highlight"
                                        />
                                    </div>
                                    <div role="cell"
                                         className="selection-table-td pl-[15px]"
                                    >
                                        <HighlightText query={`${scenario.owner?.nickname}(${scenario.owner?.email})`}
                                                       keyword={param.keyword}
                                                       highlightClassName="text-highlight"
                                        />
                                    </div>
                                    <div role="cell"
                                         className="selection-table-td pl-[15px]"
                                    >
                                        {UTCToLocalString(scenario.updated_at, 'YYYY-MM-DD HH:mm')}
                                    </div>
                                    <div role="cell"
                                         className="selection-table-td pl-[15px]"
                                    >
                                        {UTCToLocalString(scenario.created_at, 'YYYY-MM-DD HH:mm')}
                                    </div>
                                </div>
                            ))}
                            {!scenarioPage?.content.length && (
                                <div role="status"
                                     className="empty-notice"
                                     data-testid="empty-notice"
                                >
                                    {intl.formatMessage({ id: param.keyword ? "b0010" : "b0013" })}
                                </div>
                            )}
                        </div>
                    </div>

                    <PaginationBar className="mt-2"
                                   pageData={scenarioPage}
                                   onClickPage={page => setParam({ ...param, page })}
                    />
                </div>
            </div>
        </div>
    </>);
};

export default ScenarioListView;
